import React, { useState, useEffect } from 'react';
import { Search, Loader2, Calendar, User, Building2, ExternalLink, AlertTriangle, CheckCircle2, Database } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useDebounce } from '../hooks/useDebounce';

interface TenantCase {
  case_id: string;
  url: string;
  tenant_name: string;
  landlord: string;
  address: string;
  year: string;
  title: string;
  citation: string;
}

interface TenantSearchIndex {
  [tenantName: string]: TenantCase[];
}

const LTB: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<TenantCase[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchIndex, setSearchIndex] = useState<TenantSearchIndex | null>(null);
  const [indexStats, setIndexStats] = useState({ cases: 0, tenants: 0 });
  
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Load the search index on mount
  useEffect(() => {
    const loadIndex = async () => {
      try {
        const response = await fetch('/tenant_search_index.json');
        if (!response.ok) throw new Error('Failed to load index');
        const data: TenantSearchIndex = await response.json();
        setSearchIndex(data);
        
        // Calculate stats - count unique cases, not tenant-case pairs
        const tenantCount = Object.keys(data).length;
        const uniqueCases = new Set<string>();
        Object.values(data).forEach(cases => {
          cases.forEach(c => uniqueCases.add(c.case_id));
        });
        setIndexStats({ cases: uniqueCases.size, tenants: tenantCount });
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading tenant search index:', error);
        setIsLoading(false);
      }
    };
    
    loadIndex();
  }, []);

  // Search when query changes
  useEffect(() => {
    if (!searchIndex || !debouncedSearchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    
    const query = debouncedSearchQuery.toLowerCase().trim();
    const matches: TenantCase[] = [];
    
    // Search through all tenant names
    for (const [tenantName, cases] of Object.entries(searchIndex)) {
      if (tenantName.includes(query)) {
        matches.push(...cases);
      }
    }
    
    // Sort by year (newest first)
    matches.sort((a, b) => (b.year || '').localeCompare(a.year || ''));
    
    setResults(matches);
    setIsSearching(false);
  }, [debouncedSearchQuery, searchIndex]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <main className="flex-1 overflow-y-auto">
          <div className="p-8 max-w-[1600px] mx-auto">
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Loading tenant database...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="w-full">
      <main className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-[1600px] mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground tracking-tight">Tenant Background Check</h1>
              <p className="text-muted-foreground mt-1">
                Search LTB decisions to identify potential tenant risks before renting.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-2 py-1.5">
                <Database className="h-3.5 w-3.5" />
                {indexStats.cases.toLocaleString()} cases
              </Badge>
              <Badge variant="outline" className="gap-2 py-1.5">
                <User className="h-3.5 w-3.5" />
                {indexStats.tenants.toLocaleString()} tenants
              </Badge>
            </div>
          </div>

          {/* Search Box */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <Input
                  type="text"
                  placeholder="Enter tenant name to search (e.g., John Smith)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-14 text-lg rounded-xl border-neutral-200 dark:border-neutral-700"
                  autoFocus
                />
              </div>
              <p className="text-sm text-muted-foreground">
                💡 Tip: Search by full name for best results. Partial names also work.
              </p>
            </form>
          </div>

          {/* Results */}
          {isSearching && (
            <div className="flex items-center justify-center gap-3 py-8">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-muted-foreground">Searching...</span>
            </div>
          )}

          {hasSearched && !isSearching && (
            <div className="space-y-4">
              {/* Results Header */}
              <div className="flex items-center gap-3">
                {results.length > 0 ? (
                  <>
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-500">
                      <AlertTriangle className="h-5 w-5" />
                      <span className="text-lg font-semibold">
                        {results.length} case{results.length === 1 ? '' : 's'} found
                      </span>
                    </div>
                    <Badge variant="destructive" className="font-medium">
                      Review Required
                    </Badge>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-500">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="text-lg font-semibold">No records found</span>
                  </div>
                )}
              </div>

              {results.length > 0 ? (
                <div className="space-y-3">
                  {results.map((caseItem, index) => (
                    <div
                      key={`${caseItem.case_id}-${index}`}
                      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-4 hover:shadow-md hover:border-primary/30 transition-all"
                    >
                      <div className="flex items-center justify-between gap-4">
                        {/* Left side - Name and details */}
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                            <User className="h-5 w-5 text-amber-600 dark:text-amber-500" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-foreground truncate">
                              {caseItem.tenant_name}
                            </h3>
                            {caseItem.title && (
                              <p className="text-sm text-muted-foreground truncate">
                                {caseItem.title}
                              </p>
                            )}
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {caseItem.year || 'N/A'}
                              </span>
                              {caseItem.citation && (
                                <span className="text-neutral-400">
                                  {caseItem.citation}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right side - Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(caseItem.url, '_blank')}
                          className="gap-2 shrink-0"
                        >
                          <ExternalLink className="h-4 w-4" />
                          View on CanLII
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-900 p-8 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
                  <p className="text-green-800 dark:text-green-200 font-medium">
                    No LTB cases found for "{searchQuery}"
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                    This doesn't guarantee a clean history — always verify through other means.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Initial State */}
          {!hasSearched && !isSearching && (
            <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200/60 dark:border-neutral-800 shadow-sm p-12 text-center">
              <Search className="h-12 w-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                Enter a name above to search LTB decisions
              </p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                Search through {indexStats.cases.toLocaleString()} Ontario Landlord & Tenant Board cases 
                to check if a potential tenant has any eviction history.
              </p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="text-center text-xs text-muted-foreground pt-4">
            <p>Data sourced from CanLII — Ontario Landlord and Tenant Board decisions</p>
            <p className="mt-1">Results are for informational purposes only. Always conduct proper due diligence.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LTB;
